import { roundQuantity, type UnitOfMeasure } from '../schedule/unit-of-measure';
import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * Medição do serviço — quanto, e não só quantas horas.
 *
 * Hora responde "quem esteve lá"; medição responde "o que ficou pronto". São perguntas
 * diferentes, e a segunda é a que o dono da obra faz: oito horas de alvenaria podem ser
 * oito metros quadrados ou dois, e o plano não distingue os dois casos enquanto só contar
 * tempo.
 *
 * Três decisões moram aqui:
 *
 * - **a unidade é da atividade**, não da alocação. "Alvenaria — 3º Pavimento" se mede em
 *   m² para todo mundo que trabalhar nela; deixar cada lançamento escolher a unidade
 *   permitiria somar metro quadrado com metro cúbico na mesma linha;
 * - **a quantidade é do dia**, como as horas. A meta da semana é a soma dos dias, e é
 *   projeção — não um segundo número guardado que sai de sincronia com o primeiro;
 * - **a precisão é da unidade** (RN-020). `m³` tem três casas, `un` não tem nenhuma, e
 *   meia unidade de porta não existe. Quem arredonda é o catálogo, não a tela.
 */

/** Teto de sanidade: nenhuma frente de um dia mede um milhão de qualquer coisa. */
export const MAX_QUANTITY = 1_000_000;

/**
 * Valida a quantidade contra a unidade da atividade.
 *
 * `undefined` é resposta legítima e não é erro: nem toda atividade é medida, e a que é
 * nem sempre tem meta lançada. O que **não** é aceito é quantidade sem unidade — um
 * número solto de metro quadrado nenhum não significa nada, e guardá-lo criaria um dado
 * que ninguém consegue interpretar depois.
 */
export function validateQuantity(
  quantity: number | undefined,
  unit: UnitOfMeasure | undefined,
): Result<number | undefined, DomainError> {
  if (quantity === undefined) return ok(undefined);

  if (unit === undefined) {
    return err(
      domainError(
        PLANNING_ERROR.QUANTITY_WITHOUT_UNIT,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.QUANTITY_WITHOUT_UNIT]!,
        { quantity },
      ),
    );
  }

  if (!Number.isFinite(quantity) || quantity < 0) {
    return err(
      domainError(
        PLANNING_ERROR.QUANTITY_INVALID,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.QUANTITY_INVALID]!,
        { quantity },
      ),
    );
  }

  if (quantity > MAX_QUANTITY) {
    return err(
      domainError(
        PLANNING_ERROR.QUANTITY_TOO_LARGE,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.QUANTITY_TOO_LARGE]!,
        { quantity, limit: MAX_QUANTITY },
      ),
    );
  }

  // Arredondar em vez de recusar: quem digita 8,437 m² quis dizer 8,44 — recusar por
  // casa decimal seria transformar precisão de catálogo em obstáculo de digitação.
  return ok(roundQuantity(quantity, unit.decimals));
}

/**
 * O que foi feito × o que estava previsto, para uma frente.
 *
 * `undefined` em `planned` significa "sem meta lançada": o executado ainda vale, e a
 * diferença simplesmente não existe — inventar zero como meta faria toda frente sem
 * planejamento aparecer como excedida.
 */
export interface QuantityBalance {
  readonly planned?: number;
  readonly executed?: number;
  /** `executado − planejado`. Ausente quando falta um dos dois. */
  readonly difference?: number;
  /** Fração do planejado que foi executada. Ausente quando não há meta ou ela é zero. */
  readonly ratio?: number;
}

export function quantityBalance(
  planned: number | undefined,
  executed: number | undefined,
): QuantityBalance {
  if (planned === undefined || executed === undefined) {
    return {
      ...(planned === undefined ? {} : { planned }),
      ...(executed === undefined ? {} : { executed }),
    };
  }

  return {
    planned,
    executed,
    difference: executed - planned,
    ...(planned === 0 ? {} : { ratio: executed / planned }),
  };
}
